import { pageNumber, catalogHref } from '../../../../shared/public/catalog.cjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, RefreshControl, ScrollView, StyleSheet, View, Pressable } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { Link, useRouter } from '@/lib/navigation';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import StatusContainer from '@/components/StatusContainer';
import PromptGrid from '@/components/Prompts/PromptGrid';
import LoadMoreButton from '@/components/Buttons/LoadMoreButton';
import CatalogCategoryDropdown from '@/components/Dropdowns/CatalogCategoryDropdown';
import CatalogSortDropdown from '@/components/Catalog/CatalogSortDropdown';
import CatalogDiscoveryHero from '@/components/Catalog/CatalogDiscoveryHero';
import CatalogBrowseShell, { CATALOG_VIEW_MODES } from '@/components/Catalog/CatalogBrowseShell';
import CatalogMobileToolbar from '@/components/Catalog/CatalogMobileToolbar';
import PromptModalityFilter from '@/components/Prompts/PromptModalityFilter';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import { usePromptStore, useAuthStore, useModalStore } from '@/store';
import useRequireAuth from '@/hooks/useRequireAuth';
import { promptApi as promptApiSsr } from '@/lib/promptApi';
import {
  sortPrompts,
  colorForPromptCategory,
  collectModelSlugsFromPrompts,
  collectTagsFromPrompts,
} from '@/utils/prompts_catalog';

let _staticPromptsCache = null;
async function getStaticPrompts() {
  if (_staticPromptsCache) return _staticPromptsCache;
  try {
    _staticPromptsCache = await promptApiSsr.listAllPrompts();
  } catch {
    _staticPromptsCache = [];
  }
  return _staticPromptsCache;
}

if (typeof window === 'undefined') {
  getStaticPrompts().catch(() => {});
}

const createStyles = (colors, isXS, isXL, insetBottom = 0) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.solid_color_primary,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
  },
  scrollContainer: { flex: 1, width: '100%' },
  scrollContent: { width: '100%', paddingBottom: insetBottom + 40 },
  contentWrap: {
    paddingHorizontal: 0,
    maxWidth: isXL ? 1460 : 1240,
    width: '100%',
    alignSelf: 'center',
  },
  section: { marginTop: 0 },
  block: { width: '100%' },
  blockHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  filterSection: { gap: 12, marginBottom: 0 },
  filterLabel: { marginBottom: 4 },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  chipActive: {
    backgroundColor: colors.text_color_primary,
    borderColor: colors.text_color_primary,
  },
  loadMore: { marginTop: 16, alignSelf: 'center', minWidth: 220 },
  footerWrap: { paddingHorizontal: isXS ? 16 : 28 },
});

export const PromptsPage = React.memo(({ categorySlug: categorySlugProp = null } = {}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS, isXL } = useResponsiveness();
  const router = useRouter();
  const params = useLocalSearchParams();
  const initialPage = pageNumber(params?.page);
  const insets = useSafeAreaInsets();
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [catalogSort, setCatalogSort] = useState('relevant');
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const [modalityId, setModalityId] = useState('all');
  const [activeModelSlug, setActiveModelSlug] = useState(null);
  const [activeTag, setActiveTag] = useState(null);

  const favoritePromptIds = useAuthStore(s => s.user?.favoritePromptIds);
  const hasUser = useAuthStore(s => Boolean(s.user?.id));
  const requireAuth = useRequireAuth();
  const openModal = useModalStore(s => s.actions.openModal);

  const styles = useMemo(
    () => createStyles(colors, isXS, isXL, insets.bottom),
    [colors, isXS, isXL, insets.bottom],
  );

  const promptsBySlug = usePromptStore(s => s.promptsBySlug);
  const catalogSlugs = usePromptStore(s => s.catalogSlugs);
  const featuredSlugs = usePromptStore(s => s.featuredSlugs);
  const isLoading = usePromptStore(s => s.isLoadingCatalog);
  const isLoadingMore = usePromptStore(s => s.isLoadingMoreCatalog);
  const isFetched = usePromptStore(s => s.isFetchedCatalog);
  const error = usePromptStore(s => s.catalogError);
  const catalogHasMore = usePromptStore(s => s.catalogHasMore);
  const fetchCatalog = usePromptStore(s => s.fetchCatalog);
  const loadMoreCatalog = usePromptStore(s => s.loadMoreCatalog);
  const fetchFeatured = usePromptStore(s => s.fetchFeatured);
  const categories = usePromptStore(s => s.categories);
  const isFetchedCategories = usePromptStore(s => s.isFetchedCategories);
  const isLoadingCategories = usePromptStore(s => s.isLoadingCategories);
  const fetchCategories = usePromptStore(s => s.fetchCategories);

  const urlCategory = useMemo(() => {
    const raw = params?.category;
    if (Array.isArray(raw)) return raw[0] || null;
    return typeof raw === 'string' && raw ? raw : null;
  }, [params?.category]);
  const activeCategory = categorySlugProp || urlCategory || null;

  const allPrompts = useMemo(
    () => catalogSlugs.map(slug => promptsBySlug[slug]).filter(Boolean),
    [catalogSlugs, promptsBySlug],
  );

  const featuredPrompts = useMemo(
    () => featuredSlugs.map(slug => promptsBySlug[slug]).filter(Boolean),
    [featuredSlugs, promptsBySlug],
  );

  const catalogIndexBySlug = useMemo(() => {
    const map = new Map();
    for (let i = 0; i < catalogSlugs.length; i++) map.set(catalogSlugs[i], i);
    return map;
  }, [catalogSlugs]);

  const favoriteIdSet = useMemo(
    () => new Set((favoritePromptIds || []).map(String)),
    [favoritePromptIds],
  );

  const filteredPrompts = useMemo(() => {
    if (!showOnlyFavorites) return allPrompts;
    return allPrompts.filter(p => p?.id != null && favoriteIdSet.has(String(p.id)));
  }, [allPrompts, showOnlyFavorites, favoriteIdSet]);

  const sortedPrompts = useMemo(
    () => sortPrompts(filteredPrompts, catalogSort, catalogIndexBySlug),
    [filteredPrompts, catalogSort, catalogIndexBySlug],
  );

  const visibleFeaturedPrompts = useMemo(() => {
    if (showOnlyFavorites) {
      return featuredPrompts.filter(p => p?.id != null && favoriteIdSet.has(String(p.id)));
    }
    return featuredPrompts;
  }, [featuredPrompts, showOnlyFavorites, favoriteIdSet]);

  const modelOptions = useMemo(() => collectModelSlugsFromPrompts(allPrompts), [allPrompts]);
  const tagOptions = useMemo(() => collectTagsFromPrompts(allPrompts), [allPrompts]);

  const buildServerFilters = useCallback(() => {
    const filters = {};
    const trimmed = searchQuery.trim();
    if (trimmed) filters.q = trimmed;
    if (activeCategory) filters.category = activeCategory;
    if (activeTag) filters.tag = activeTag;
    if (activeModelSlug) filters.defaultModel = activeModelSlug;
    if (modalityId === 'new') filters.isNew = true;
    if (modalityId === 'image') filters.outputType = 'image';
    if (modalityId === 'video') filters.outputType = 'video';
    if (!trimmed && !activeTag && !activeModelSlug && modalityId === 'all' && initialPage > 1) filters.skip = (initialPage - 1) * 20;
    return filters;
  }, [searchQuery, activeCategory, activeTag, activeModelSlug, modalityId, initialPage]);

  useEffect(() => {
    fetchFeatured(8).catch(() => {});
    if (!isFetchedCategories && !isLoadingCategories) {
      fetchCategories().catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      fetchCatalog(buildServerFilters()).catch(() => {});
    }, searchQuery.trim() ? 280 : 0);
    return () => clearTimeout(handle);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, activeCategory, activeTag, activeModelSlug, modalityId, initialPage]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        fetchCatalog(buildServerFilters()),
        fetchFeatured(8),
        fetchCategories(),
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [fetchCatalog, fetchFeatured, fetchCategories, buildServerFilters]);

  const handleLoadMore = useCallback(() => {
    loadMoreCatalog().catch(() => {});
  }, [loadMoreCatalog]);

  const handlePromptPress = useCallback((prompt) => {
    if (prompt?.slug) router.push(`/prompts/${prompt.slug}`);
  }, [router]);

  const handleCategoryChange = useCallback((slug) => {
    if (!slug) router.push('/prompts');
    else router.push(`/prompts/category/${encodeURIComponent(slug)}`);
  }, [router]);

  const handleRequestPrompt = useCallback(() => {
    openModal('FeedbackModal', {
      isOpen: true,
      initialContent: t('pages.prompts.requestPromptPrefill')
        || "I'd like to request a prompt recipe for:\n\n(Describe the image or video you want — input type, style, example idea, etc.)",
    });
  }, [openModal, t]);

  const handleModalityChange = useCallback((id) => {
    setModalityId(id);
  }, []);

  const categoryDropdownCategories = useMemo(() => {
    const bySlug = new Map();
    for (const cat of categories || []) {
      if (!cat?.slug) continue;
      const count = Number(cat?.stats?.promptCount) || 0;
      const prev = bySlug.get(cat.slug);
      if (!prev || count > (prev.count ?? 0)) {
        bySlug.set(cat.slug, { ...cat, count });
      }
    }
    // Hide empty categories — they clutter the picker and duplicate rows when
    // stats are stale (0) alongside a live recount for the same slug.
    return [...bySlug.values()].filter((cat) => cat.count > 0);
  }, [categories]);

  const promptsCatalogSortOptions = useMemo(() => [
    { id: 'relevant', label: t('pages.tools.catalogSortRelevant') || 'Relevant', icon: 'bars-staggered' },
    { id: 'popular', label: t('pages.tools.catalogSortPopular') || 'Popular', icon: 'fire' },
    { id: 'name-asc', label: t('pages.tools.catalogSortNameAsc') || 'Name A–Z', icon: 'arrow-down-a-z' },
    { id: 'name-desc', label: t('pages.tools.catalogSortNameDesc') || 'Name Z–A', icon: 'arrow-up-z-a' },
    { id: 'category', label: t('pages.tools.catalogSortCategory') || 'By category', icon: 'layer-group' },
  ], [t]);

  const showTagFilter = tagOptions.length > 1;
  const tagFilterOptions = useMemo(() => [
    { id: 'all', label: t('pages.prompts.filterAll') || 'All', icon: 'tags' },
    ...tagOptions.map((tag) => ({
      id: tag,
      label: tag.replace(/-/g, ' '),
      icon: 'tag',
    })),
  ], [tagOptions, t]);

  const handleTagFilterChange = useCallback((id) => {
    setActiveTag(id === 'all' ? null : id);
  }, []);

  const handlePromptsSortSelect = useCallback((sortId) => {
    if (sortId === 'favorites') {
      requireAuth(
        () => { if (!showOnlyFavorites) setShowOnlyFavorites(true); },
        {
          source: 'prompts_favorites_sort',
          title: 'Sign in to see your favorite recipes',
          subtitle: 'Favorites are tied to your account so they follow you across devices.',
          icon: 'heart',
        },
      );
      return;
    }
    setCatalogSort(sortId);
    if (showOnlyFavorites) setShowOnlyFavorites(false);
  }, [showOnlyFavorites, requireAuth]);

  // Renders inside a DropdownMenu.Content, so on native it has to be a raw
  // DropdownMenu.Item — the native menu drops anything else.
  const promptsSortFooter = useMemo(() => {
    if (!hasUser) return null;
    const label = t('pages.prompts.myFavorites') || 'My favorites';

    if (Platform.OS !== 'web') {
      return (
        <DropdownMenu.Item key="favorites" onSelect={() => handlePromptsSortSelect('favorites')}>
          {showOnlyFavorites && <DropdownMenu.ItemIcon ios={{ name: 'checkmark' }} />}
          <DropdownMenu.ItemTitle>{label}</DropdownMenu.ItemTitle>
        </DropdownMenu.Item>
      );
    }

    return (
      <DropdownMenuItem
        key="favorites"
        icon="heart"
        title={label}
        isActive={showOnlyFavorites}
        onSelect={() => handlePromptsSortSelect('favorites')}
      />
    );
  }, [hasUser, t, showOnlyFavorites, handlePromptsSortSelect]);

  const seoStructuredData = useMemo(() => {
    const cacheList = Array.isArray(_staticPromptsCache) ? _staticPromptsCache : [];
    const liveList = allPrompts || [];
    const sourceList = liveList.length ? liveList : cacheList;
    const itemList = sourceList.slice(0, 100).map((p, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      url: `https://gab.ai/prompts/${p.slug}`,
      name: p?.meta?.title || p?.name,
    }));
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          name: 'Prompt Library',
          description: 'Curated one-click recipes for AI image and video generation on Gab AI.',
          url: `https://gab.ai${catalogHref('prompts', activeCategory, initialPage)}`,
          isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: 'https://gab.ai' },
          mainEntity: { '@type': 'ItemList', itemListElement: itemList },
        },
      ],
    };
  }, [allPrompts, activeCategory, initialPage]);

  const renderFilterChips = (options, active, onSelect, getLabel = (x) => x) => (
    <View style={styles.chipRow}>
      <Pressable
        onPress={() => onSelect(null)}
        style={[styles.chip, active == null && styles.chipActive]}
      >
        <Text size="xs" weight={active == null ? 'bold' : 'medium'} style={active == null ? { color: colors.solid_color_primary } : undefined}>
          {t('pages.prompts.filterAll')}
        </Text>
      </Pressable>
      {options.map((opt) => {
        const id = typeof opt === 'string' ? opt : opt.slug;
        const label = typeof opt === 'string' ? getLabel(opt) : (opt.name || opt.slug);
        const isActive = active === id;
        return (
          <Pressable
            key={id}
            onPress={() => onSelect(isActive ? null : id)}
            style={[styles.chip, isActive && styles.chipActive]}
          >
            <Text size="xs" weight={isActive ? 'bold' : 'medium'} style={isActive ? { color: colors.solid_color_primary } : undefined}>
              {label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );

  const showLoadMore = catalogHasMore && !error && !isLoading && sortedPrompts.length > 0;
  const isSearching = !!searchQuery.trim();

  return (
    <View style={styles.container}>
      {!categorySlugProp && (
        <MetaHead
          title={t('pages.prompts.title') || 'Prompt Library — One-Click AI Image & Video Recipes'}
          description={t('pages.prompts.description') || 'Browse curated recipes for images and video. Upload a photo or describe your idea, generate instantly, then keep refining in chat.'}
          canonical={catalogHref('prompts', null, initialPage)}
          keywords={t('pages.prompts.seoKeywords') || 'AI prompt library, image prompts, video prompts, AI art generator, photo to video'}
          structuredData={seoStructuredData}
        />
      )}
      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        refreshControl={(
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={colors.text_color_secondary}
          />
        )}
      >
        <View style={styles.contentWrap}>
          <CatalogDiscoveryHero
            topline={t('pages.prompts.topline') || 'Prompt Library'}
            subtitle={t('pages.prompts.subtitle') || 'Ready-to-run recipes for images and video — copy, customize, and generate in one click.'}
            headerAction={{
              label: t('pages.prompts.requestPrompt') || 'Request a Prompt',
              icon: 'lightbulb',
              onPress: handleRequestPrompt,
            }}
            search={{
              value: searchQuery,
              onChangeText: setSearchQuery,
              placeholder: t('pages.prompts.searchPlaceholder') || 'Search the library…',
              testID: 'prompts-catalog-search',
            }}
          />
          <View style={styles.section}>
            <CatalogBrowseShell
              isXS={isXS}
              bodyGap={10}
              hideRail
              suppressMobilePills
              toolbar={{
                hideViewToggle: true,
                trailing: (
                  <>
                    {showTagFilter ? (
                      <CatalogSortDropdown
                        value={activeTag || 'all'}
                        options={tagFilterOptions}
                        onChange={handleTagFilterChange}
                        label={t('pages.prompts.filterByTag') || 'Tag'}
                      />
                    ) : null}
                    <CatalogCategoryDropdown
                      categories={categoryDropdownCategories}
                      activeCategorySlug={showOnlyFavorites ? null : activeCategory}
                      onChangeCategory={(slug) => {
                        if (showOnlyFavorites) setShowOnlyFavorites(false);
                        handleCategoryChange(slug);
                      }}
                      allOptionLabel={t('pages.prompts.allPrompts') || 'All recipes'}
                      defaultTriggerLabel={t('pages.tasks.categoriesEyebrow') || 'Categories'}
                      getCategoryTint={(slug) => colorForPromptCategory(slug).fg}
                      formatCountLabel={(n) => (
                        n === 1
                          ? (t('pages.prompts.countOne') || '1 recipe')
                          : (t('pages.prompts.countMany') || '%COUNT% recipes').replace('%COUNT%', String(n))
                      )}
                      getCategoryCount={(cat) => cat.count}
                    />
                    <CatalogSortDropdown
                      value={showOnlyFavorites ? 'favorites' : catalogSort}
                      options={promptsCatalogSortOptions}
                      onChange={handlePromptsSortSelect}
                      label={t('pages.tools.catalogSort') || 'Sort'}
                      footer={promptsSortFooter}
                      extraActiveOptions={hasUser ? [{ id: 'favorites', label: t('pages.prompts.myFavorites') || 'My favorites', icon: 'heart' }] : []}
                    />
                  </>
                ),
                mobile: (
                  <CatalogMobileToolbar
                    chipSlots={[
                      ...(showTagFilter ? [
                        <CatalogSortDropdown
                          key="prompts-tag"
                          value={activeTag || 'all'}
                          options={tagFilterOptions}
                          onChange={handleTagFilterChange}
                          label={t('pages.prompts.filterByTag') || 'Tag'}
                        />,
                      ] : []),
                      <CatalogCategoryDropdown
                        key="prompts-cat"
                        variant="toolbarChip"
                        categories={categoryDropdownCategories}
                        activeCategorySlug={showOnlyFavorites ? null : activeCategory}
                        onChangeCategory={(slug) => {
                          if (showOnlyFavorites) setShowOnlyFavorites(false);
                          handleCategoryChange(slug);
                        }}
                        allOptionLabel={t('pages.prompts.allPrompts') || 'All recipes'}
                        defaultTriggerLabel={t('pages.tasks.categoriesEyebrow') || 'Categories'}
                        mobileTriggerLabel={t('pages.tasks.categoriesEyebrow') || 'Categories'}
                        getCategoryTint={(slug) => colorForPromptCategory(slug).fg}
                      />,
                    ]}
                  />
                ),
              }}
            >
              <View style={styles.filterSection}>
                <PromptModalityFilter activeId={modalityId} onChange={handleModalityChange} />
                {modelOptions.length > 1 ? (
                  <View>
                    <Text size="xs" color="tertiary" style={styles.filterLabel}>
                      {t('pages.prompts.filterByModel') || 'Filter by model'}
                    </Text>
                    {renderFilterChips(modelOptions, activeModelSlug, setActiveModelSlug)}
                  </View>
                ) : null}
              </View>

              {!isSearching && !activeCategory && !showOnlyFavorites && visibleFeaturedPrompts.length > 0 ? (
                <View style={[styles.block, { marginBottom: 20 }]}>
                  <View style={styles.blockHeader}>
                    <Text weight="bold" size="m">{t('pages.tools.featured') || 'Featured'}</Text>
                  </View>
                  <PromptGrid prompts={visibleFeaturedPrompts} onPromptPress={handlePromptPress} />
                </View>
              ) : null}

              <View style={styles.block}>
                {error && !isLoading && !sortedPrompts.length ? (
                  <StatusContainer type="error" message={error} actionText={t('pages.common.retry') || 'Retry'} onAction={onRefresh} />
                ) : isLoading && !isFetched ? (
                  <PromptGrid isLoading placeholderCount={isXS ? 6 : 9} />
                ) : !sortedPrompts.length ? (
                  <StatusContainer
                    type="empty"
                    message={
                      showOnlyFavorites && !(favoritePromptIds || []).length
                        ? (t('pages.prompts.noFavoritePromptsYet') || 'Save recipes with the heart to see them here.')
                        : showOnlyFavorites
                          ? (t('pages.prompts.noFavoritePromptsMatch') || 'No saved favorites match this filter.')
                          : (t('pages.prompts.noResults') || 'No recipes match your filters.')
                    }
                  />
                ) : (
                  <>
                    <PromptGrid prompts={sortedPrompts} onPromptPress={handlePromptPress} />
                    {showLoadMore ? (
                      <LoadMoreButton href={!searchQuery.trim() && !activeTag && !activeModelSlug && modalityId === 'all' && !showOnlyFavorites ? catalogHref('prompts', activeCategory, initialPage + Math.ceil(catalogSlugs.length / 20)) : undefined} onPress={handleLoadMore} isLoading={isLoadingMore} isLarge style={styles.loadMore} />
                    ) : null}
                  </>
                )}
              </View>
            </CatalogBrowseShell>
          </View>
          {!hasUser ? (
            <View style={[styles.footerWrap, { paddingBottom: 8 }]}>
              <Link href="/signup" asChild>
                <Pressable
                  style={{
                    alignItems: 'center',
                    paddingVertical: 20,
                    paddingHorizontal: 24,
                    borderRadius: 16,
                    backgroundColor: colors.solid_color_block,
                    borderWidth: 1,
                    borderColor: colors.border_color_secondary,
                  }}
                >
                  <Text weight="bold" size="m">Save and run prompts with a free account</Text>
                  <Text color="secondary" size="s" style={{ marginTop: 6 }}>
                    Sign up to copy recipes and run them in chat.
                  </Text>
                </Pressable>
              </Link>
            </View>
          ) : null}
          <View style={styles.footerWrap}>
            <AppDashboardFooter />
          </View>
        </View>
      </ScrollView>
    </View>
  );
});

PromptsPage.displayName = 'PromptsPage';

export default function PromptsRoute() {
  return <PromptsPage />;
}
